
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import styles from '../../core-exchange.module.scss';
export const metadata = {
  toc: true,
  subnav: true,
  title: 'Backend token exchange',
  layout: 'guide',
  description: 'Migrate existing connections to Core Exchange without requiring users to reauthenticate',
  parentTocLevel: 2,
  childTocLevel: 3,
  secondLevelToc: true,
  alwaysExpand: true
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Callout = makeShortcode("Callout");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <div className={styles.page}>
      <Header title='Backend token exchange' subtitle={metadata.description} mdxType="Header" />
      <h2 {...{
        "id": "is-this-for-you"
      }}>{`Is this for you?`}</h2>
      <p>{`Backend token exchange lets Plaid migrate an existing Plaid Exchange connection to Core Exchange server to server, without sending the user back through your OAuth flow. For the rest of what that move involves, see `}<a parentName="p" {...{
          "href": "/core-exchange/docs/migrating-from-plaid-exchange"
        }}>{`Migrating from Plaid Exchange`}</a>{`.`}</p>
      <p>{`This isn't a self-serve Dashboard feature: your Plaid contact configures your exchange endpoint and migration cohort internally, so raise it with them before you begin planning a migration. This page describes what your endpoint needs to do once that's set up, not how to request it.`}</p>
      <h2 {...{
        "id": "how-it-works"
      }}>{`How it works`}</h2>
      <p>{`Plaid calls a token exchange endpoint you provide, sending the access or auth token from the user's existing connection. Your endpoint validates that token, identifies the user, and returns a set of Core Exchange OAuth tokens, without the user taking any action.`}</p>
      <Callout mdxType="Callout">
        <p>{`The path Plaid calls is whatever endpoint your Plaid contact configures for your institution; there's no fixed route every institution must implement. Examples in this guide use `}<inlineCode parentName="p">{`/token`}</inlineCode>{` as a placeholder.`}</p>
      </Callout>
      <h2 {...{
        "id": "request"
      }}>{`Request`}</h2>
      <p>{`Plaid sends a `}<inlineCode parentName="p">{`POST`}</inlineCode>{` request with a JSON body containing a single field:`}</p>
      <table>
        <thead parentName="table">
          <tr parentName="thead">
            <th parentName="tr" {...{
              "align": null
            }}>{`Field`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Description`}</th>
          </tr>
        </thead>
        <tbody parentName="table">
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`token`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`The user's existing Plaid Exchange auth token for the connection being migrated.`}</td>
          </tr>
        </tbody>
      </table>
      <pre><code parentName="pre" {...{
          "className": "language-bash",
          "metastring": "# Example request, using /token as a placeholder for your configured endpoint",
          "#": true,
          "Example": true,
          "request,": true,
          "using": true,
          "/token": true,
          "as": true,
          "a": true,
          "placeholder": true,
          "for": true,
          "your": true,
          "configured": true,
          "endpoint": true
        }}>{`curl -X POST 'https://your-institution.com/token' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "token": "eyJhbGciOiJI..."
  }'
`}</code></pre>
      <h3 {...{
        "id": "authenticating-the-request"
      }}>{`Authenticating the request`}</h3>
      <p>{`Plaid sends no OAuth client credentials to this endpoint. It presents a client certificate if your institution is configured for mTLS, along with any headers configured for your institution; your Plaid Exchange `}<inlineCode parentName="p">{`X-PLAID-CLIENT-ID`}</inlineCode>{` and `}<inlineCode parentName="p">{`X-PLAID-SECRET`}</inlineCode>{` aren't sent automatically. Tell your Plaid contact what your endpoint expects.`}</p>
      <h2 {...{
        "id": "response"
      }}>{`Response`}</h2>
      <p>{`Return these fields. `}<inlineCode parentName="p">{`access_token`}</inlineCode>{`, `}<inlineCode parentName="p">{`refresh_token`}</inlineCode>{`, and `}<inlineCode parentName="p">{`expires_in`}</inlineCode>{` work the same way they do in a standard `}<a parentName="p" {...{
          "href": "/core-exchange/docs/authentication/oauth-flow/#step-4---plaid-requests-an-access-token"
        }}>{`OAuth token exchange`}</a>{`, but this response carries `}<inlineCode parentName="p">{`token_type`}</inlineCode>{` and a direct `}<inlineCode parentName="p">{`user_id`}</inlineCode>{` instead of an `}<inlineCode parentName="p">{`id_token`}</inlineCode>{`:`}</p>
      <table>
        <thead parentName="table">
          <tr parentName="thead">
            <th parentName="tr" {...{
              "align": null
            }}>{`Field`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Description`}</th>
          </tr>
        </thead>
        <tbody parentName="table">
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`access_token`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`An opaque string. Plaid presents this as a bearer token to your Core Exchange API.`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`token_type`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`The token type, as in a standard OAuth token response.`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`refresh_token`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`An opaque, revocable token Plaid uses to request new access tokens on an ongoing basis.`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`expires_in`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`The lifetime of the access token, in seconds.`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`scope`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Optional. The granted scope, if your integration uses scoped tokens.`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`user_id`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`A stable, opaque identifier for the user, matching the `}<a parentName="td" {...{
                "href": "/core-exchange/docs/authentication/oauth-server-setup/#unique-user-identifier-consistency-key"
              }}>{`consistency key`}</a>{` your integration returns elsewhere.`}</td>
          </tr>
        </tbody>
      </table>
      <pre><code parentName="pre" {...{
          "className": "language-json",
          "metastring": "# Example response",
          "#": true,
          "Example": true,
          "response": true
        }}>{`{
  "access_token": "agstynmdygjdghabrgraeh...",
  "token_type": "bearer",
  "refresh_token": "dhcsrtjsrgayvkdisfdgntshstu...",
  "expires_in": 900,
  "user_id": "2347456437346745"
}
`}</code></pre>
      <h3 {...{
        "id": "errors"
      }}>{`Errors`}</h3>
      <p>{`Return errors in the standard OAuth format used elsewhere in Core Exchange: an `}<inlineCode parentName="p">{`error`}</inlineCode>{` string from the `}<a parentName="p" {...{
          "href": "/core-exchange/docs/authentication/oauth-flow/#token-error-codes"
        }}>{`token error codes`}</a>{`, plus an optional `}<inlineCode parentName="p">{`error_description`}</inlineCode>{`. Return `}<inlineCode parentName="p">{`401`}</inlineCode>{` if you can't validate the token or map it to a user.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-json",
          "metastring": "# Example error response",
          "#": true,
          "Example": true,
          "error": true,
          "response": true
        }}>{`{
  "error": "invalid_grant",
  "error_description": "Token has expired"
}
`}</code></pre>
      <h2 {...{
        "id": "handling-repeated-and-retried-requests"
      }}>{`Handling repeated and retried requests`}</h2>
      <p>{`A single user can have several connections to different apps, each backed by the same underlying legacy token. Plaid calls your endpoint once per connection, so expect repeated calls presenting the same token.`}</p>
      <ul>
        <li parentName="ul"><strong parentName="li">{`Don't deactivate the original token on first use.`}</strong>{` Subsequent calls with that same token, for other connections, need to keep working.`}</li>
        <li parentName="ul"><strong parentName="li">{`Don't treat a repeated or invalid token as a security event.`}</strong>{` Failed or duplicate exchange attempts during a migration are expected traffic, not signals of compromise; triggering account locks or user-facing security notifications from this endpoint will interrupt migrations that would otherwise succeed.`}</li>
        <li parentName="ul"><strong parentName="li">{`Make retrying safe.`}</strong>{` Plaid retries `}<inlineCode parentName="li">{`500`}</inlineCode>{`, `}<inlineCode parentName="li">{`502`}</inlineCode>{`, `}<inlineCode parentName="li">{`503`}</inlineCode>{`, and `}<inlineCode parentName="li">{`504`}</inlineCode>{` responses with the same token. Exchanging the same valid token twice should return usable tokens, not an error.`}</li>
      </ul>
      <h2 {...{
        "id": "after-migrating"
      }}>{`After migrating`}</h2>
      <p>{`Once a connection has been exchanged, Plaid uses the returned `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` and `}<inlineCode parentName="p">{`refresh_token`}</inlineCode>{` the same way it would for any Core Exchange connection established through the standard OAuth flow. See `}<a parentName="p" {...{
          "href": "/core-exchange/docs/authentication/oauth-flow"
        }}>{`Implementing the OAuth flow`}</a>{` for how Plaid uses `}<inlineCode parentName="p">{`refresh_token`}</inlineCode>{`, and the `}<a parentName="p" {...{
          "href": "/core-exchange/docs/onboarding-guide/#migrations"
        }}>{`migration guide`}</a>{` for how this fits into a broader migration timeline.`}</p>
    </div>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "is-this-for-you",
        level: 2,
        title: "Is this for you?",
      },
{
        id: "how-it-works",
        level: 2,
        title: "How it works",
      },
{
        id: "request",
        level: 2,
        title: "Request",
      },
{
        id: "authenticating-the-request",
        level: 3,
        title: "Authenticating the request",
      },
{
        id: "response",
        level: 2,
        title: "Response",
      },
{
        id: "errors",
        level: 3,
        title: "Errors",
      },
{
        id: "handling-repeated-and-retried-requests",
        level: 2,
        title: "Handling repeated and retried requests",
      },
{
        id: "after-migrating",
        level: 2,
        title: "After migrating",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
